import React, { useState, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { Button, Tag } from 'antd';
import moment from 'moment';
import 'moment/min/locales';
import { isMobile } from 'react-device-detect';
import {
  blockApi,
  entityMonitoringApi,
  sensorApi,
} from 'farmx-api';
import { useLocation } from 'react-router';
import { debounce } from 'lodash';
import { adminOperationPaths } from '../constants';

const nonLoraNodes = ['field_node', 'dragino_cellular', 'milesight_cellular'];
const loraNodes = ['lora_sensor_node', 'lora_dragino'];

const stateColorsWithLightShades = {
  good: '#c7fcdbff',
  bad: '#fcc6c9ff',
  moderate: '#f0eea9ff',
}

export const deviceStatus = {
  ONLINE: 'online',
  DEGRADED: 'degraded',
}

export const deviceStatusTagColor = {
  online: 'green',
  degraded: 'orange',
  offline: 'red'
}

/* eslint-disable no-useless-escape */
export const validateEmail = (email) => !!email.match(/^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/);

// Ref link: https://docs.sentry.io/platforms/javascript/guides/react/
export const sentryInit = () => {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    tracesSampleRate: 1.0,
    integrations: [new Sentry.BrowserTracing()],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
};

/**
 * @param {*} feedBack - FeedBack object with name, event_id, email, comments and score
 * @param {*} callBack - function to return response
 */
export const submitUserFeedback = (feedBack, callBack) => {
  const organizationSlug = process.env.REACT_APP_SENTRY_ORGANIZATION_SLUG;
  const projectSlug = process.env.REACT_APP_SENTRY_PROJECT_SLUG;
  const DSN = process.env.REACT_APP_SENTRY_DSN;
  const feedBackURL = `https://sentry.io/api/0/projects/${organizationSlug}/${projectSlug}/user-feedback/`;

  const headers = {
    Authorization: `DSN ${DSN}`,
    'Content-Type': 'application/json',
  };

  fetch(feedBackURL, {
    method: 'POST',
    headers,
    body: JSON.stringify(feedBack),
  }).then((response) => callBack(response)).catch(() => callBack(false));
};

export const getPercentForFraction = (value) => Math.round(Number(value) * 1000) / 10;
export const roundToNearestPercent = (value) => Math.round(Number(value));

export function setMomentLocale(lang) {
  const browserLang = navigator.language;
  return moment.locale(lang === 'system' ? browserLang : lang);
}

function getTitleCaseString(inputString) {
  const anomalyTypeWords = inputString.replace('_', ' ').split(' ');

  for (let i = 0; i < anomalyTypeWords.length; i += 1) {
    anomalyTypeWords[i] = anomalyTypeWords[i].charAt(0).toUpperCase()
     + anomalyTypeWords[i].slice(1);
  }
  return anomalyTypeWords.join(' ');
}

export function getHumanReadableAnomalyType(anomalyType) {
  if (anomalyType) {
    return getTitleCaseString(anomalyType);
  }
  return null;
}

export function recommendationAnomalyFilter(data) {
  return data?.filter((d) => d?.type !== 'imagery_anomaly' && d?.type !== 'ground_mission')
    .reduce((a, obj) => (obj || a), undefined);
}

export function getRecommendationIdentifier(recommendation) {
  return recommendation.block && recommendation.type
    ? `${recommendation.block}-${recommendation.type}${recommendation.type === 'imagery_anomaly'
      ? `-${recommendation.anomalyType}` : ''}`
    : `${recommendation.block}-ground_mission`;
}

export const compareByName = (a, b) => (a?.name?.toLowerCase()
  ?.localeCompare(b?.name?.toLowerCase()));

// Function to extract the numeric part from the string
export const extractNumber = (str) => {
  if (str?.match(/\d+/)) return parseInt(str.match(/\d+/)[0], 10);
  return null;
};

// Custom sorting function
export const sortArrayOfObjectsByProperty = (arr, property = 'name', isDescending) => {
  let sortedArr = arr?.sort((a, b) => {
    if (extractNumber(a?.[property])) {
      return extractNumber(a?.[property]) - extractNumber(b?.[property]);
    }
    return a?.[property]?.toLowerCase()?.localeCompare(b?.[property]?.toLowerCase());
  });
  if (isDescending) {
    sortedArr = arr?.sort((a, b) => {
      if (extractNumber(b?.[property])) {
        return extractNumber(b?.[property]) - extractNumber(a?.[property]);
      }
      return b?.[property]?.toLowerCase()?.localeCompare(a?.[property]?.toLowerCase());
    });
  }
  return sortedArr;
};

export function renderRecommendationDateDetail(recommendation, t) {
  const { dateCreated } = recommendation || {};
  const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const recommendationCreatedAt = dateCreated
    ? moment(dateCreated).tz(localTimezone).format('MMMM D, YYYY [at] h:mm a z') : 'unknown';

  return (
    <small className="text-margin">
      <em>
        {`${t('Generated on')} ${recommendationCreatedAt}`}
      </em>
    </small>
  );
}

export function checkIsAllRanch(type) {
  return type === 'entity';
}
/**
 * Function to render table row without headers
 * @param {*} tableData - array of object with keys like ['label', 'value', 'info']
 * @param {*} showInfo - boolean value to show clickable info icon
 * @returns - table without headers with default style
 */
export function renderTableWithoutHeader(tableData, showInfo) {
  return (
    <div className="irrigation-equation-table-border">
      <table className="table table-striped">
        <tbody>
          {tableData.map((d) => (
            <tr>
              <td className={`align-left ${d?.highlight ? 'tr-bold-text' : ''}`}>
                {d?.label}
              </td>
              <td className={`align-right ${d?.highlight ? 'tr-bold-text' : ''} table-tooltip`}>
                {d?.value}
                {d?.title && <span className="tooltiptext">{d?.title}</span>}
                {d?.edit && d?.edit}
              </td>
              {showInfo ? (
                <td className="align-center">{d?.info || null}</td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Function to get the duration in hours format
export function parseDuration(duration, t) {
  if (duration === undefined || duration === null) {
    return t('?');
  }
  // Split the string by space
  const parts = duration.split(' ');
  let days = 0; // Default to 0 days if not present
  let timePart = duration; // Assume the entire string is time part

  // If the string contains a day part
  if (parts.length === 2) {
    const dayPart = parts[0];
    days = parseInt(dayPart, 10); // Extract days
    timePart = parts[1]; // The second part is the time
  }

  // Use moment to parse the time part and convert it to hours
  const hours = moment.duration(timePart).asHours();

  // Calculate total hours
  const totalHours = days * 24 + hours;

  if (Math.abs(totalHours) < 10) {
    return Math.round(totalHours * 10) / 10;
  }

  return Math.round(totalHours);
}

// Function to get the values in depth units by user settings
export function parseWater(water, fromUnit, getUserUnits, toUnitByLength, t) {
  if (water === undefined || water === null) {
    return t('?');
  }
  const { value: depthValue } = getUserUnits(water, fromUnit,
    undefined, { decimalPlaces: 2 }, toUnitByLength);
  if (Math.abs(depthValue) < 10) {
    return Math.round(depthValue * 100) / 100;
  }
  return Math.round(depthValue);
}

// To convert the value from depth "inches" to volume "gallons"
export function convertToGallons(value, fromUnit, blockAreaInM2, getUserUnits) {
  // Convert value in "inches" to "meters"
  const { value: valueInMeters } = getUserUnits(Number(value), fromUnit,
    undefined, { decimalPlaces: 5 }, 'meters');

  // Convert "meters" to cubic meters
  const volumeInCubicMeters = blockAreaInM2 * valueInMeters;

  // Cubic meters to liters
  const { value: valueInLiters } = getUserUnits(volumeInCubicMeters, 'cubic_meters',
    undefined, { decimalPlaces: 5 }, 'liters');

  const gallonsText = isMobile ? 'gal' :'gallons';
  // liters to gallons
  const { value: valueInGallons, label } = getUserUnits(valueInLiters, 'liters',
    undefined, { decimalPlaces: 5 }, gallonsText);
  return { value: valueInGallons, label };
}

// To convert the value in from the units "volume (gallons/liters)",
// "depth (inches)", "duration (hours)"
export function getValueByUserUnit(value, fromUnit, toUnit, userVolumeUnit,
  blockApplicationRate, blockAreaInM2, getUserUnits, toUnitByLength) {
  let convertedValue;
  let convertedLabel;
  // To convert value to "gallons"
  if (toUnit === 'volume' && userVolumeUnit === 'gallons') {
    const { value: valueInGallons, label } = convertToGallons(value, fromUnit,
      blockAreaInM2, getUserUnits);

    convertedValue = valueInGallons;
    convertedLabel = label;
  }
  // To convert value to "liters"
  if (toUnit === 'volume' && userVolumeUnit === 'liters') {
    const { value: valueInGallons } = convertToGallons(value, fromUnit,
      blockAreaInM2, getUserUnits);

    // Then convert gallons to liters
    const { value: valueInLiters, label } = getUserUnits(valueInGallons, 'gallons',
      undefined, { decimalPlaces: 5 }, 'liters');
    convertedValue = valueInLiters;
    convertedLabel = label;
  }
  // To convert value to "hours"
  if (toUnit === 'duration') {
    const { value: valueInGallons } = convertToGallons(value, fromUnit,
      blockAreaInM2, getUserUnits);

    // To get hours: gallons / application rate
    const valueInHours = valueInGallons / Number(blockApplicationRate);
    convertedValue = valueInHours;
    convertedLabel = isMobile ? 'hrs' : 'hours';
  }

  // To convert value from hours to "gallons"
  if (fromUnit ==='hours' && toUnit === 'volume' && userVolumeUnit === 'gallons') {
    // convert hours to gallons by multiply the hours * application rate
    convertedValue = (Number(value) || 0) * blockApplicationRate;
    convertedLabel = 'gallons';
  }

  // To convert value from hours to "liters"
  if (fromUnit ==='hours' && toUnit === 'volume' && userVolumeUnit === 'liters') {
    // convert hours to gallons by multiply the hours * application rate
    const hoursToGallons = (Number(value) || 0) * blockApplicationRate;

    // Then convert gallons to liters
    const { value: valueInLiters, label } = getUserUnits(hoursToGallons, 'gallons',
      undefined, { decimalPlaces: 5 }, 'liters');
    convertedValue = valueInLiters;
    convertedLabel = label;
  }

  // To convert value from hours to "inches" with volume "gallons"
  if (fromUnit ==='hours' && toUnit === 'depth' && userVolumeUnit === 'gallons') {
    // convert hours to gallons by multiply the hours * application rate
    const hoursToGallons = (Number(value) || 0) * Number(blockApplicationRate);

    // First convert gallons to cubic inches
    const { value: valueInCubicInches } = getUserUnits(hoursToGallons, 'gallons',
      undefined, { decimalPlaces: 5 }, 'cubic_inches');

    // 1 gallon = 231 cubic inches
    // 1 m2 = 1550.0031 square inches
    const areaToSquareInches = blockAreaInM2 * 1550.0031;

    // Third convert value to inches
    const valueInInches = valueInCubicInches / areaToSquareInches;

    // Convert value to user legth format from inches
    const { value: depthValue, label } = getUserUnits(valueInInches, 'inches',
      undefined, { decimalPlaces: 5 }, toUnitByLength);
    convertedValue = depthValue;
    convertedLabel = label;
  }

  // To convert value from hours to "inches" with volume "liters"
  if (fromUnit ==='hours' && toUnit === 'depth' && userVolumeUnit === 'liters') {
    // convert hours to gallons by multiply the hours * application rate
    const hoursToGallons = (Number(value) || 0) * Number(blockApplicationRate);

    // First convert gallons to liters
    const { value: valueInLiters } = getUserUnits(hoursToGallons, 'gallons',
      undefined, { decimalPlaces: 5 }, 'liters');

    // Second convert liters to cubic meters
    const { value: valueInCubicInches } = getUserUnits(valueInLiters, 'liters',
      undefined, { decimalPlaces: 5 }, 'cubic_inches');

    // 1 m2 = 1550.0031 square inches
    const areaToSquareInches = blockAreaInM2 * 1550.0031;

    // Third convert value to inches
    const valueInInches = valueInCubicInches / areaToSquareInches;

    // Convert value to user legth format from inches
    const { value: depthValue, label } = getUserUnits(valueInInches, 'inches',
      undefined, { decimalPlaces: 5 }, toUnitByLength);
    convertedValue = depthValue;
    convertedLabel = label;
  }

  return { value: convertedValue, label: convertedLabel };
}

// To update the custom kc value
export function updateCustomKcValue(blockId, customKcValue, callBack) {
  blockApi.updateCustomKcForBlock(blockId, customKcValue).then((success) => {
    callBack(true, success);
  }, (error) => {
    callBack(false, error);
  });
}

// To check if 2 arrays have same values
export function isSameList(list1, list2){
  if(!list1 || !list2) return false;
  if(list1.length !== list2.length) return false;
  return list1.map((item) => list2.includes(item))
    .reduce((a, p) => a && p, true);
}

export function checkIsActionNeeded(currentRecommendation) {
  let isActionNeeded = true
  if (currentRecommendation?.freezeType === 'action'
    && currentRecommendation.isFrozen) {
      // Planned tab
      isActionNeeded = false;
  }
  return isActionNeeded;
}

export const useCheckIsAdminOperations = () => {
  const location = useLocation();
  const path = location?.pathname || "";
  return adminOperationPaths.some(adminPath => path.includes(adminPath));
};


export const getNodeHealthData = (sensorType, sensorId, variables,
  startDate, endDate, callBack) => {
  sensorApi.loadSensorData(sensorType, sensorId, variables,
    startDate, endDate, undefined, 1).then((response) => {
      callBack(response?.data, true);
    }).catch((err) => {
      callBack(err, false);
    })
}

const isValidSelectorId = (id) => id !== undefined
  && id !== null
  && id !== ''
  && id !== 'undefined'
  && id !== 'null';

export const getNodeStatusData = (type, id, paramStr, callBack) => {
  if (!type || !isValidSelectorId(id)) {
    callBack(
      new Error('Selector type and id are required to load sensor status.'),
      false
    );
    return;
  }

  sensorApi.loadSensorStatusForSelectorTypeAndIdWithParam(type, id, paramStr)
  .then((response) => {
    callBack(response?.data, true);
  }).catch((err) => {
    callBack(err, false);
  })
}

export const getNodeAssociatedSensors = async (configList, callBack) => {
  const allRequests = configList?.map((config) => sensorApi.loadNodeAssociatedSensors(config?.identifier));
  const responses = await Promise.allSettled(allRequests);
  await callBack(responses
    ?.filter((response) => response.status === 'fulfilled' && response.value.status === 200)
    ?.map((response) => ({ ...response.value.data })));
}

export const getNodeMetaData = (identifier, callBack) => {
  sensorApi.loadNodeMetaDataByNodeIdentifier(identifier).then((response) => {
    callBack(response?.data, true);
  }).catch((err) => {
    callBack(err, false);
  })
}

export const useGetMaxWidth = () => {
  const [maxWidth, setMaxWidth] = useState(`${window.innerWidth - 200}px`);

  useEffect(() => {
    const handleResize = debounce(() => {
      setMaxWidth(`${window.innerWidth - 200}px`);
    }, 150);

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
      handleResize.cancel();
    };
  }, []);

  return maxWidth;
}

export function getColorForRecommendationTime(record) {
  let color = undefined;
  let tooltipText = '';
  const key = `${record?.id}_${record?.entity_id}_${record?.ranch_id}`

  const lastReported = record?.last_recommendation_time ? moment(record?.last_recommendation_time) : 0;
  const now = moment();
  const diffInHours = now.diff(lastReported, 'hours');

  if (diffInHours < 24) {
    color = stateColorsWithLightShades.good;
    tooltipText = 'Less than 24 hours';
  }
  else if (diffInHours > 72) {
    color = stateColorsWithLightShades.bad;
    tooltipText = 'More than 3 days';
  }
  else if (diffInHours < 72) {
    color = stateColorsWithLightShades.moderate;
    tooltipText = 'Less than 3 days';
  }
  return { color, key, tooltipText };
}

export function getColorForId(record) {
  let color = undefined;
  let tooltipText = '';
  const key = `${record?.id}_${record?.identifier}`

  const lastReported = record?.latest_date ? moment(record?.latest_date) : 0;
  const now = moment();
  const diffInHours = now.diff(lastReported, 'hours');

  if (record?.online) {
    color = stateColorsWithLightShades.good;
    tooltipText = 'Online';
  }
  else if (!record?.online && diffInHours > 24) {
    color = stateColorsWithLightShades.bad;
    tooltipText = 'Offline more than 24 hours';
  }
  else if (!record?.online && diffInHours < 24) {
    color = stateColorsWithLightShades.moderate;
    tooltipText = 'Offline for less than 24 hours';
  }
  return { color, key, tooltipText };
}

export function renderFieldValue(fieldValue, t) {
  const isInvalid =
    fieldValue === undefined ||
    fieldValue === null ||
    fieldValue === '' ||
    fieldValue === Infinity ||
    Number.isNaN(fieldValue);

  return isInvalid ? '-' : fieldValue;
}

export function getSensorName(sensors, record) {
  const { type, identifier, name } = record || {};
  const sensor = sensors?.[`${type}/${identifier}`];
  const { name: sensorName } = sensor || {};
  return name || sensorName || 'No Name';
}

export const getTableField = (sensors, t) => ({
  index: {
    width: 60,
    render: (_, __, index) => index + 1,
  },
  name: {
    width: 100,
    render: (_, record) => {
      const { color, key, tooltipText } = getColorForId(record);
      return <Tag title={t(tooltipText)} color={color} key={key}>{getSensorName(sensors, record)}</Tag>;
    },
  },
  id: {
    width: 100,
    render: (_, record) => {
      const { color, key, tooltipText } = getColorForId(record);
      return <Tag title={t(tooltipText)} color={color} key={key}>{record.id}</Tag>;
    },
  },
  online: {
    width: 100,
    render: (_, record) => (record?.online ? t('Online') : t('Offline')),
  },
  identifier: { width: 140 },
  latestDate: {
    width: 120,
    render: (_, record) => (record?.latestDate
      ? (moment(record?.latestDate).format('YYYY-MM-DD HH:mm:ss')) : renderFieldValue(undefined, t)),
  },
  latest_date: {
    width: 120,
    render: (_, record) => (record?.latest_date
      ? (moment(record?.latest_date).format('YYYY-MM-DD HH:mm:ss')) : renderFieldValue(undefined, t)),
  },
  date: {
    width: 150,
    render: (_, record) => (record?.date
      ? (moment(record?.date).format('YYYY-MM-DD HH:mm:ss')) : renderFieldValue(undefined, t)),
  },
  provisioned: {
    width: 80,
    render: (_, record) => (record?.provisioned ? t('True') : t('False')),
  },
  chargingStatus: {
    width: 150,
    render: (_, record) => (record?.chargingStatus
      ? t(record?.chargingStatus) : renderFieldValue(undefined, t)),
  },
  rebootTime: {
    width: 200,
    render: (_, record) => (record?.rebootTime
      ? (moment.unix(Number(record?.rebootTime)).format('YYYY-MM-DD HH:mm:ss')) : renderFieldValue(undefined, t)),
  },
  rebootReason: {
    width: 200,
    render: (_, record) => (record?.rebootReason
      ? t(record?.rebootReason) : renderFieldValue(undefined, t)),
  },
  sub_type: {
    width: 150,
    render: (_, record) => renderFieldValue(record?.sub_type, t),
  },
  batteryVoltage: {
    width: 150,
    render: (_, record) => renderFieldValue(record?.batteryVoltage, t),
  },
  dataCount24hr: {
    width: 200,
    render: (_, record) => renderFieldValue(record?.dataCount24hr, t),
  },
  externalVoltage: {
    width: 150,
    render: (_, record) => renderFieldValue(record?.externalVoltage, t),
  },
  heapMemoryFree: {
    width: 150,
    render: (_, record) => renderFieldValue(record?.heapMemoryFree, t),
  },
  dataCountExpected: {
    width: 150,
    render: (_, record) => renderFieldValue(record?.dataCountExpected, t),
  },
  externalMemoryFree: {
    width: 250,
    render: (_, record) => renderFieldValue(record?.externalMemoryFree, t),
  },
  external_memory_free: {
    width: 200,
    render: (_, record) => renderFieldValue(record?.external_memory_free, t),
  },
  battery_voltage: {
    width: 150,
    render: (_, record) => renderFieldValue(record?.battery_voltage, t),
  },
  data_count_24hr: {
    width: 150,
    render: (_, record) => renderFieldValue(record?.data_count_24hr, t),
  },
  external_voltage: {
    width: 150,
    render: (_, record) => renderFieldValue(record?.external_voltage, t),
  },
  heap_memory_free: {
    width: 200,
    render: (_, record) => renderFieldValue(record?.heap_memory_free, t),
  },
  data_count_expected: {
    width: 150,
    render: (_, record) => renderFieldValue(record?.data_count_expected, t),
  },
  core_temp: {
    width: 80,
    render: (_, record) => renderFieldValue(record?.core_temp, t),
  },
  coreTemp: {
    width: 80,
    render: (_, record) => renderFieldValue(record?.coreTemp, t),
  },
  charging_current: {
    width: 200,
    render: (_, record) => renderFieldValue(record?.charging_current, t),
  },
  solar_voltage: {
    width: 200,
    render: (_, record) => renderFieldValue(record?.solar_voltage, t),
  },
  reboot_reason: {
    width: 200,
    render: (_, record) => renderFieldValue(record?.reboot_reason, t),
  },
  network_type: {
    width: 200,
    render: (_, record) => renderFieldValue(record?.network_type, t),
  },
  chargingCurrent: {
    width: 200,
    render: (_, record) => renderFieldValue(record?.chargingCurrent, t),
  },
  solarVoltage: {
    width: 200,
    render: (_, record) => renderFieldValue(record?.solarVoltage, t),
  },
  networkType: {
    width: 200,
    render: (_, record) => renderFieldValue(record?.networkType, t),
  },
  fwAPartFree: {
    width: 300,
    render: (_, record) => renderFieldValue(record?.fwAPartFree, t),
  },
  configAPartFree: {
    width: 350,
    render: (_, record) => renderFieldValue(record?.configAPartFree, t),
  },
  fwBPartFree: {
    width: 300,
    render: (_, record) => renderFieldValue(record?.fwBPartFree, t),
  },
  configBPartFree: {
    width: 300,
    render: (_, record) => renderFieldValue(record?.configBPartFree, t),
  },
  sensorDataPartFree: {
    width: 300,
    render: (_, record) => renderFieldValue(record?.sensorDataPartFree, t),
  },
  systemLogsPartFree: {
    width: 300,
    render: (_, record) => renderFieldValue(record?.systemLogsPartFree, t),
  },
  scratchAreaPartFree: {
    width: 300,
    render: (_, record) => renderFieldValue(record?.scratchAreaPartFree, t),
  },
  upTime: {
    width: 150,
    render: (_, record) => renderFieldValue(record?.upTime, t),
  },
  rssi: {
    width: 60,
    render: (_, record) => renderFieldValue(record?.rssi, t),
  },
  provider: {
    width: 100,
    render: (_, record) => renderFieldValue(record?.provider, t),
  },
  fw_version: {
    width: 100,
    render: (_, record) => renderFieldValue(record?.fw_version, t),
  },
  git_info: {
    width: 100,
    render: (_, record) => renderFieldValue(record?.git_info, t),
  },
  online24: {
    width: 100,
    render: (_, record) => (record?.provisioned ? t('True') : t('False')),
  },
});

export const getMonitoringTableField = (onClick, t) => ({
  index: {
    width: 60,
    render: (_, __, index) => index + 1,
  },
  issues: {
    width: 150,
    render: (_, record) => {
      return <Button
       type="link"
       key={`key-${record?.issues}-${record?.id}`}
       onClick={() => onClick(record, 'issues')}
       disabled={!record?.issues}
       title={t('Click to view')}
      >{record?.issues}</Button>;
    },
  },
  device_status_logs: {
    width: 150,
    render: (_, record) => {
      return <Button
       type="link"
       key={`key-${record?.ranch_id}-${record?.entity_id}-${record?.id}`}
       onClick={() => onClick(record, 'device_status_logs')}
       disabled={!record?.device_status_logs?.length}
      >{t('View')}</Button>;
    }
  },
  last_recommendation_time: {
    width: 200,
    render: (_, record) => {
      const { color, key, tooltipText } = getColorForRecommendationTime(record);
      return record.last_recommendation_time
       ? <Tag title={t(tooltipText)} color={color} key={key}>
        {moment(record.last_recommendation_time).format('YYYY-MM-DD HH:mm:ss')}
        </Tag>
       : ' - ';
    },
  },
  region: {
    width: 60,
    render: (_, record) => renderFieldValue(record?.region, t),
  },
})

export function updateChartHeight(chartHeight, wrapper, isOverAll = false) {
  const newHeight = `${chartHeight}px`;

  // To update charts height when clicking on default chart size button
  // Ex: small, medium, large
  if (isOverAll) {
    const resizeableElems = document.querySelectorAll('.resizeable')
    const chartDivElems = document.querySelectorAll('.chart-component-main');
    const allElems = [...resizeableElems, ...chartDivElems];

    allElems?.forEach((el) => {
      el.style.height = newHeight;
    });

    // Update all the chart size
    if (window.charts?.length) {
      window.charts?.forEach((chart) => {
        if (chart?.axes) {
          chart.setSize(null, Math.abs(chartHeight - 10), false);
        }
      });
    }
    return;
  }
  
  if (!wrapper) return;

  // To update charts height when resizing
  const innerDivs = wrapper.querySelectorAll('.chart-component-wrapper');
  const resizeableElem = wrapper.querySelector('.resizeable');
  if (resizeableElem) {
    resizeableElem.style.height = newHeight;
  }
  const chartComponentMain = wrapper.querySelectorAll('.chart-component-main');
  if (chartComponentMain?.length) {
    chartComponentMain?.forEach((el) => {
      el.style.height = newHeight;
    });
  }

  // Update only the resized chart size
  if (window.charts?.length) {
    window.charts?.forEach((chart) => {
      const chartContainer = chart?.container;

      const isInsideCorrelated = Array.from(innerDivs).some(div =>
        div.contains(chartContainer)
      );

      if (isInsideCorrelated && chart?.axes) {
        chart.setSize(null, Math.abs(chartHeight - 10), false);
      }
    });
  }
}

export const getNodeMetaDataFiled = (t) => ({
  id: {
    width: 50,
    render: (_, record) => renderFieldValue(record?.id, t),
  },
  identifier: {
    width: 150,
    render: (_, record) => renderFieldValue(record?.identifier, t),
  },
  node_type: {
    width: 100,
    render: (_, record) => renderFieldValue(record?.node_type, t),
  },
  imei: {
    width: 100,
    render: (_, record) => renderFieldValue(record?.imei, t),
  },
  iccid: {
    width: 100,
    render: (_, record) => renderFieldValue(record?.iccid, t),
  },
  lora_eui: {
    width: 150,
    render: (_, record) => renderFieldValue(record?.lora_eui, t),
  },
  bluetooth_device_address: {
    width: 150,
    render: (_, record) => renderFieldValue(record?.bluetooth_device_address, t),
  },
  manufacture_datetime: {
    width: 150,
    render: (_, record) => record?.manufacture_datetime
      ? moment(record?.manufacture_datetime).format('YYYY-MM-DD HH:mm:ss')
      : renderFieldValue(undefined, t),
  },
  manufacturer: {
    width: 150,
    render: (_, record) => renderFieldValue(record?.manufacturer, t),
  },
  manufacturer_location: {
    width: 150,
    render: (_, record) => renderFieldValue(record?.manufacturer_location, t),
  },
  manufacturer_assembly_country: {
    width: 150,
    render: (_, record) => renderFieldValue(record?.manufacturer_assembly_country, t),
  },
  batch_number: {
    width: 150,
    render: (_, record) => renderFieldValue(record?.batch_number, t),
  },
  line_number: {
    width: 150,
    render: (_, record) => renderFieldValue(record?.line_number, t),
  },
  product_code: {
    width: 150,
    render: (_, record) => renderFieldValue(record?.product_code, t),
  },
  station_id: {
    width: 150,
    render: (_, record) => renderFieldValue(record?.station_id, t),
  },
  operator_id: {
    width: 150,
    render: (_, record) => renderFieldValue(record?.operator_id, t),
  },
  flash_tool_sw_version: {
    width: 150,
    render: (_, record) => renderFieldValue(record?.flash_tool_sw_version, t),
  },
  farmx_part_number: {
    width: 150,
    render: (_, record) => renderFieldValue(record?.farmx_part_number, t),
  },
  modem_model: {
    width: 150,
    render: (_, record) => renderFieldValue(record?.modem_model, t),
  },
  modem_fw_ver: {
    width: 150,
    render: (_, record) => renderFieldValue(record?.modem_fw_ver, t),
  },
  hw_ver: {
    width: 150,
    render: (_, record) => renderFieldValue(record?.hw_ver, t),
  },
  sw_ver: {
    width: 150,
    render: (_, record) => renderFieldValue(record?.sw_ver, t),
  },
})

export function getSensorDetailURLPath(type) {
  let sensorPath = '';
  if (type === 'water_pressure') sensorPath = 'pressure-sensor';
  else if (type === 'aquacheck_soil' || type === 'pixl_soil') sensorPath = 'soil-sensor';
  else if (type === 'water_flow_analog' || type === 'water_flow') sensorPath = 'flow-sensor';
  else if (type === 'weather_station') sensorPath = 'weather';
  else if (type === 'farmx_dendrometer') sensorPath = 'plant-sensor';
  else if (type === 'farmx_infrared') sensorPath = 'infrared-sensor';
  else if (type === 'valve') sensorPath = 'valve-sensor';
  else if (type === 'vfd') sensorPath = 'pump';
  else if (type === 'temperature_humidity') sensorPath = 'temperature-humidity';

  return sensorPath;
}

export function getSensorStatus(type, identifier, sensorStatusList) {
  const [sensorStatus] = sensorStatusList?.filter((item) => (type === item?.type
    && identifier === item?.identifier))
  const { online } = sensorStatus || {};

  return online ? 'Online' : 'Offline';
}

export function sortByDateWithKey(data, key, descending = false) {
  return [...(data || [])].sort((a, b) => {
    const dateA = a?.[key] ? new Date(a?.[key]).getTime() : -Infinity;

    const dateB = b?.[key] ? new Date(b?.[key]).getTime() : -Infinity;

    return !descending ? dateA - dateB : dateB - dateA;
  });
}

export const getEntityMonitoringData = (requestObj, callBack) => {
  entityMonitoringApi.getEntityMonitoringData(requestObj).then((response) => {
    callBack(response?.data, true);
  }).catch((err) => {
    callBack(err, false);
  })
}

export const rebootNodeByIdentifier = (identifier, sub_type, callBack) => {
  if (nonLoraNodes.includes(sub_type)) {
    sensorApi.rebootNode(identifier).then((response) => {
      callBack(response?.data, true);
    }).catch((err) => {
      callBack(err, false);
    })
  }

  if (loraNodes.includes(sub_type)) {
    sensorApi.rebootLoraNode(identifier).then((response) => {
      callBack(response?.data, true);
    }).catch((err) => {
      callBack(err, false);
    })
  }
}
